import React, { useState } from 'react';
import {
  Palette,
  Check,
  Building,
  CreditCard,
  FileText,
  Save,
  Eye,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { useGst } from '../../context/GstContext';
import { InvoiceTemplateId, MerchantBillingSettings } from '../../types/gst';
import { INVOICE_TEMPLATES } from '../../services/billingService';
import { InvoiceTemplateRenderer } from '../../components/billing/InvoiceTemplateRenderer';
import { toast } from '../../components/ui';

export const TemplatesPage: React.FC = () => {
  const {
    billingSettings,
    saveBillingSettings,
    merchantInvoices,
    activeGstin,
    clientLegalName
  } = useGst();

  const [activeTemplate, setActiveTemplate] = useState<InvoiceTemplateId>(
    billingSettings?.defaultTemplateId || 'CLASSIC_GST'
  );

  // Form settings state
  const [companyName, setCompanyName] = useState<string>(
    billingSettings?.companyName || clientLegalName || 'Verve AI Labs Pvt Ltd'
  );
  const [tradeName, setTradeName] = useState<string>(billingSettings?.tradeName || 'Verve AI Labs');
  const [phone, setPhone] = useState<string>(billingSettings?.phone || '+91 98200 99881');
  const [email, setEmail] = useState<string>(billingSettings?.email || 'billing@verveailabs.com');
  const [address, setAddress] = useState<string>(
    billingSettings?.address || 'Unit 402, Quantum Heights, BKC, Bandra East, Mumbai - 400051'
  );
  const [bankName, setBankName] = useState<string>(billingSettings?.bankName || 'HDFC Bank Ltd');
  const [accountNumber, setAccountNumber] = useState<string>(
    billingSettings?.accountNumber || '50200088991122'
  );
  const [ifscCode, setIfscCode] = useState<string>(billingSettings?.ifscCode || 'HDFC0000240');
  const [upiId, setUpiId] = useState<string>(billingSettings?.upiId || 'verveailabs@hdfcbank');
  const [defaultTerms, setDefaultTerms] = useState<string>(
    billingSettings?.defaultTerms ||
      '1. Payment due within 15 days.\n2. Overdue payments subject to 18% p.a. interest.\n3. Goods/Services non-refundable.'
  );

  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Use either first invoice or fallback sample invoice
  const sampleInvoice = merchantInvoices[0] || {
    id: 'sample_inv',
    clientId: 'sample',
    invoiceNumber: 'INV/2025/101',
    invoiceDate: '2025-07-28',
    dueDate: '2025-08-12',
    invoiceType: 'B2B',
    financialYear: '2025-26',
    taxPeriod: 'July',
    sellerGstin: activeGstin || '27AABCU9603R1ZM',
    sellerLegalName: companyName,
    sellerTradeName: tradeName,
    sellerAddress: address,
    sellerPhone: phone,
    sellerEmail: email,
    sellerStateCode: '27',
    customerName: 'Finova Technologies Pvt Ltd',
    customerGstin: '27AABCF9944M1ZQ',
    customerPhone: '9820123456',
    customerEmail: 'finance@finova.in',
    customerBillingAddress: 'Cyber One, Vashi, Navi Mumbai, Maharashtra - 400703',
    placeOfSupply: '27-Maharashtra',
    isInterState: false,
    lineItems: [
      {
        id: '1',
        description: 'Enterprise AI Agentic Workspace (10 Seats)',
        hsnSac: '998313',
        quantity: 1,
        unit: 'MONTHS',
        unitPrice: 85000,
        discountPercent: 10,
        discountAmount: 8500,
        taxableAmount: 76500,
        gstRate: 18,
        cgstAmount: 6885,
        sgstAmount: 6885,
        igstAmount: 0,
        cessAmount: 0,
        totalAmount: 90270
      }
    ],
    subtotalTaxable: 76500,
    totalCgst: 6885,
    totalSgst: 6885,
    totalIgst: 0,
    totalCess: 0,
    totalTax: 13770,
    roundOff: 0,
    grandTotal: 90270,
    status: 'ISSUED',
    paidAmount: 0,
    balanceDue: 90270,
    templateId: activeTemplate,
    termsAndConditions: defaultTerms,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      const updated: MerchantBillingSettings = {
        clientId: billingSettings?.clientId || 'default',
        companyName,
        tradeName,
        gstin: activeGstin || '27AABCU9603R1ZM',
        pan: (activeGstin || '27AABCU9603R1ZM').substring(2, 12),
        phone,
        email,
        address,
        stateCode: '27',
        stateName: 'Maharashtra',
        bankName,
        accountNumber,
        ifscCode,
        branchName: 'BKC Branch',
        upiId,
        defaultTerms,
        invoicePrefix: billingSettings?.invoicePrefix || 'INV/25-26/',
        nextInvoiceSequence: billingSettings?.nextInvoiceSequence || 101,
        defaultTemplateId: activeTemplate
      };

      await saveBillingSettings(updated);
      toast.success(`Default template set to ${activeTemplate} & Merchant settings saved.`);
    } catch (err: any) {
      toast.error('Failed to save settings: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Title */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Palette className="h-6 w-6 text-cyan-400" />
            <span>Invoice Templates & Brand Customizer</span>
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Choose from 5 statutory GST layouts & configure your company bank details, UPI ID, and terms
          </p>
        </div>

        <button
          onClick={handleSaveSettings}
          disabled={isSaving}
          className="flex items-center gap-1.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-2 text-xs font-bold transition shadow-sm shadow-cyan-500/25"
        >
          <Save className="h-4 w-4" />
          <span>{isSaving ? 'Saving...' : 'Save Default Template'}</span>
        </button>
      </div>

      {/* Template Chooser Grid */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {INVOICE_TEMPLATES.map((tmpl) => {
          const isSelected = activeTemplate === tmpl.id;
          return (
            <button
              key={tmpl.id}
              onClick={() => setActiveTemplate(tmpl.id)}
              className={`p-3.5 rounded-2xl text-left border transition relative flex flex-col justify-between ${
                isSelected
                  ? 'border-cyan-500 bg-cyan-950/30 shadow-[0_0_20px_rgba(6,182,212,0.15)] ring-1 ring-cyan-500/50'
                  : 'border-white/10 bg-[#131316] hover:border-white/20 hover:bg-white/[0.02]'
              }`}
            >
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
                    {tmpl.category}
                  </span>
                  {isSelected && (
                    <span className="rounded-full bg-cyan-500 p-0.5 text-black">
                      <Check className="h-3 w-3 stroke-[3]" />
                    </span>
                  )}
                </div>
                <div className="font-bold text-white text-xs mt-1.5">{tmpl.name}</div>
                <div className="text-[10px] text-slate-400 mt-0.5 leading-snug">{tmpl.tagline}</div>
              </div>
              <div className="text-[9px] text-slate-500 mt-3 pt-2 border-t border-white/5">
                {tmpl.recommendedFor}
              </div>
            </button>
          );
        })}
      </div>

      {/* Main Container: Settings (Left) & Live Preview (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Merchant Settings Form */}
        <div className="lg:col-span-4 rounded-2xl border border-white/10 bg-[#131316] p-5 space-y-4 text-xs">
          <div className="text-xs font-bold uppercase text-cyan-400 border-b border-white/5 pb-2">
            Merchant & Bank Branding
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-medium text-slate-300 block mb-1">Company / Legal Name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-1.5 text-white text-xs focus:outline-none focus:border-cyan-500"
              />
            </div>

            <div>
              <label className="text-[11px] font-medium text-slate-300 block mb-1">Trade / Brand Name</label>
              <input
                type="text"
                value={tradeName}
                onChange={(e) => setTradeName(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-1.5 text-white text-xs focus:outline-none focus:border-cyan-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] font-medium text-slate-300 block mb-1">Phone</label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-2.5 py-1.5 text-white text-xs font-mono focus:outline-none focus:border-cyan-500"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-300 block mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-cyan-500"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-medium text-slate-300 block mb-1">Address</label>
              <textarea
                rows={2}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-1.5 text-white text-xs focus:outline-none focus:border-cyan-500 resize-none"
              />
            </div>

            <div className="border-t border-white/5 pt-3 space-y-3">
              <div className="font-bold text-slate-300 text-[11px] uppercase tracking-wider">
                Bank & UPI Routing Details
              </div>

              <div>
                <label className="text-[11px] font-medium text-slate-300 block mb-1">Bank Name</label>
                <input
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-1.5 text-white text-xs focus:outline-none focus:border-cyan-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] font-medium text-slate-300 block mb-1">A/C Number</label>
                  <input
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-2.5 py-1.5 text-white text-xs font-mono focus:outline-none focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-slate-300 block mb-1">IFSC Code</label>
                  <input
                    type="text"
                    value={ifscCode}
                    onChange={(e) => setIfscCode(e.target.value)}
                    className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-2.5 py-1.5 text-white text-xs font-mono uppercase focus:outline-none focus:border-cyan-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] font-medium text-slate-300 block mb-1">UPI ID (for instant QR pay)</label>
                <input
                  type="text"
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-1.5 text-white text-xs font-mono text-cyan-300 focus:outline-none focus:border-cyan-500"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-medium text-slate-300 block mb-1">Default Terms & Conditions</label>
              <textarea
                rows={3}
                value={defaultTerms}
                onChange={(e) => setDefaultTerms(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-1.5 text-white text-xs focus:outline-none focus:border-cyan-500 resize-none leading-relaxed"
              />
            </div>
          </div>
        </div>

        {/* Live Template Preview */}
        <div className="lg:col-span-8 rounded-2xl border border-white/10 bg-[#08090b] p-6 overflow-y-auto max-h-[800px]">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-white/5">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-cyan-400" />
              <span className="font-bold text-white text-xs">Live Template Preview: {activeTemplate}</span>
            </div>
            <div className="text-[11px] text-slate-400">
              Matches print-ready output exactly
            </div>
          </div>

          <InvoiceTemplateRenderer
            invoice={{
              ...sampleInvoice,
              sellerLegalName: companyName,
              sellerTradeName: tradeName,
              sellerPhone: phone,
              sellerEmail: email,
              sellerAddress: address,
              termsAndConditions: defaultTerms
            }}
            settings={{
              ...billingSettings,
              clientId: 'preview',
              companyName,
              tradeName,
              gstin: activeGstin || '27AABCU9603R1ZM',
              pan: (activeGstin || '27AABCU9603R1ZM').substring(2, 12),
              phone,
              email,
              address,
              stateCode: '27',
              stateName: 'Maharashtra',
              bankName,
              accountNumber,
              ifscCode,
              branchName: 'BKC Branch',
              upiId,
              defaultTerms,
              invoicePrefix: 'INV/25-26/',
              nextInvoiceSequence: 101,
              defaultTemplateId: activeTemplate
            }}
            overrideTemplateId={activeTemplate}
          />
        </div>
      </div>
    </div>
  );
};
